import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { GET_EXPENSE_CATEGORY_OPTIONS } from "../../graphql/expenseCategory";

export interface ExpenseCategory {
  id: string;
  name: string;
  level: number;
}

interface ExpenseCategorySearchAutoCompleteProps {
  title?: string;
  /** Field height. Defaults to the DS default, which is what every other call site wants. */
  size?: "md" | "lg";
  expenseCategory: ExpenseCategory | null;
  onSelectExpenseCategory: (expenseCategory: ExpenseCategory | null) => void;
  hideClearButton?: boolean;
}

/**
 * The category picker. The whole tree arrives in one query and filtering is
 * local, so this is a plain `searchable` Select: the DS marks the matched runs
 * from its own input, and `depth` gets it to draw the indent and the elbow that
 * used to be a hand-rolled `paddingLeft` plus an `<Elbow />` svg.
 */
const ExpenseCategorySearchAutoComplete: React.FC<
  ExpenseCategorySearchAutoCompleteProps
> = ({
  title = "",
  expenseCategory,
  onSelectExpenseCategory,
  size,
  hideClearButton = false,
}) => {
  const { data, loading } = useQuery(GET_EXPENSE_CATEGORY_OPTIONS);

  const categories: ExpenseCategory[] = useMemo(
    () => data?.expenseCategories ?? [],
    [data],
  );

  const options: SelectOption[] = useMemo(
    () =>
      categories.map((category) => ({
        id: category.id,
        label: category.name,
        depth: category.level,
      })),
    [categories],
  );

  // The saved value may not be in the list yet while the query is in flight,
  // and the DS resolves a label only by finding the id in `options`.
  const optionsWithSelection = useMemo(() => {
    if (!expenseCategory?.id) return options;
    return options.some((option) => option.id === expenseCategory.id)
      ? options
      : [
          {
            id: expenseCategory.id,
            label: expenseCategory.name,
            depth: expenseCategory.level,
          },
          ...options,
        ];
  }, [options, expenseCategory]);

  return (
    <Select
      size={size}
      label={title || undefined}
      aria-label={title ? undefined : "Expense category"}
      searchable
      loading={loading}
      clearable={!hideClearButton}
      options={optionsWithSelection}
      value={expenseCategory?.id ?? null}
      // The seeded selection is not in `categories` until the query lands,
      // so it resolves from the seed rather than reporting null.
      onChange={(id) =>
        onSelectExpenseCategory(
          categories.find((category) => category.id === id) ??
            (expenseCategory?.id === id ? expenseCategory : null),
        )
      }
    />
  );
};

export default ExpenseCategorySearchAutoComplete;
